/**
 * The thread — one agent's timeline rendered as messages (spec #44 §1.3).
 *
 * The entries come from the provider's cache, which is the same page the
 * single head poller refreshes when the watermark moves. This component
 * fetches nothing itself: a thread that polled on its own would be the
 * N-pollers bug the whole state layer exists to avoid.
 */

import { SkeletonBodyText, Text } from '@shopify/polaris';

import { useWorkspace } from '../../hooks/useWorkspace';
import { Composer } from './Composer';
import { TimelineEntryCard } from './TimelineEntryCard';
import { WorkspaceEmpty } from './WorkspaceEmpty';
import styles from './workspace.module.css';

export function AgentThread({ domain }: { domain: string }) {
  const { agents, entriesFor, loading, errors } = useWorkspace();
  const entries = entriesFor(domain);
  const agent = agents.find((a) => a.domain === domain);

  return (
    <div data-testid={`thread-${domain}`}>
      <div className={styles.laneHead}>
        <span className={styles.laneTitle}>
          {agent?.display_name ?? domain}
        </span>
        <span className={styles.subtle}>
          everything this agent did, newest last
        </span>
      </div>

      <div className={styles.thread}>
        {errors.thread ? (
          <WorkspaceEmpty kind="lane-error" reason={errors.thread} />
        ) : loading.thread && entries.length === 0 ? (
          <SkeletonBodyText lines={6} />
        ) : entries.length === 0 ? (
          <WorkspaceEmpty kind="no-cycle" />
        ) : (
          // Newest LAST, the way a channel reads.
          [...entries]
            .sort(
              (a, b) =>
                Date.parse(a.occurred_at) - Date.parse(b.occurred_at)
            )
            .map((entry) => (
              <TimelineEntryCard key={entry.id} entry={entry} />
            ))
        )}
      </div>

      {agent?.configured === false ? (
        <Text as="p" tone="subdued">
          This agent is not set up yet, so it has nothing to say.
        </Text>
      ) : null}

      <Composer domain={domain} />
    </div>
  );
}
